import React, { useState, useEffect } from 'react';
import TaskForm from './TaskForm';

const Tasks = ({ data, onUpdate }) => {
  const { tasks } = data;
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // Listen for cloud sync completion
  useEffect(() => {
    const handleCloudSyncComplete = (event) => {
      console.log('📱 Cloud sync completed - FORCE RELOADING Tasks data');
      // Trigger parent to reload data
      if (onUpdate) {
        // Get fresh data from localStorage
        const savedTasks = localStorage.getItem('tasksData_' + (data.farmId || 'default'));
        if (savedTasks) {
          try {
            const parsedTasks = JSON.parse(savedTasks);
            const actualTasks = parsedTasks.data || parsedTasks; // Handle wrapped format
            onUpdate(actualTasks);
          } catch (e) {
            console.log('Could not parse tasks data for refresh');
          }
        }
      }
    };
    
    window.addEventListener('cloudSyncComplete', handleCloudSyncComplete);
    window.addEventListener('tasksDataUpdated', handleCloudSyncComplete);
    
    return () => {
      window.removeEventListener('cloudSyncComplete', handleCloudSyncComplete);
      window.removeEventListener('tasksDataUpdated', handleCloudSyncComplete);
    };
  }, [onUpdate, data.farmId]);
  
  const handleAddClick = () => {
    setEditingTask(null);
    setShowForm(true);
  };
  
  const handleEditClick = (task) => {
    setEditingTask(task);
    setShowForm(true);
  };
  
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTask(null);
  };
  
  const handleSaveTask = (savedTask) => {
    let updatedTasks;
    
    if (editingTask) {
      // Update existing task
      updatedTasks = tasks.map(task => 
        task.id === savedTask.id ? savedTask : task
      );
    } else {
      // Add new task
      updatedTasks = [...tasks, savedTask];
    }
    
    // Call the parent component's update function with the new tasks data
    onUpdate(updatedTasks);
    
    // Close the form
    setShowForm(false);
    setEditingTask(null);
  };
  
  const handleCompleteTask = (taskId) => {
    const updatedTasks = tasks.map(task => 
      task.id === taskId 
        ? { ...task, status: 'Completed' } 
        : task
    );
    
    onUpdate(updatedTasks);
  };
  
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex justify-between items-center">
        <h3 className="font-semibold text-lg">Tasks</h3>
        <button 
          onClick={handleAddClick}
          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
        >
          + New Task
        </button>
      </div>
      
      {showForm && (
        <div className="p-4 border-b">
          <TaskForm 
            task={editingTask}
            onSave={handleSaveTask}
            onCancel={handleFormCancel}
          />
        </div>
      )}
      
      <div className="p-4">
        {tasks.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No tasks available. Click "New Task" to add one.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Task</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Assigned To</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Due Date</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Priority</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tasks.map(task => (
                <tr key={task.id}>
                  <td className="py-3 text-sm font-medium">{task.title}</td>
                  <td className="py-3 text-sm">{task.assignedTo}</td>
                  <td className="py-3 text-sm">
                    <span className={task.dueDate === 'Today' ? 'text-red-600 font-medium' : ''}>
                      {task.dueDate}
                    </span>
                  </td>
                  <td className="py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      task.priority === 'High' ? 'bg-red-100 text-red-800' : 
                      task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 
                      task.status === 'Completed' ? 'bg-green-100 text-green-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.status}
                    </span>
                  </td>
                  <td className="py-3 text-sm">
                    {task.status !== 'Completed' && (
                      <button 
                        onClick={() => handleCompleteTask(task.id)}
                        className="text-blue-600 hover:text-blue-800 font-medium mr-2"
                      >
                        Complete
                      </button>
                    )}
                    <button 
                      onClick={() => handleEditClick(task)}
                      className="text-gray-600 hover:text-gray-800 font-medium"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Tasks;